import React from 'react';
import { useIntl } from 'react-intl';
import type { LyricSection, LyricSuggestion } from 'fym-shared/src/music24/lyricSections';
import { TypedMemo } from '../../../../../common/types';
import { LyricLineRow, type LyricRowTag } from './LyricLineRow';
import { lyricPanelMessages } from './lyricPanelMessages';
import { sectionName } from './sectionName';
import type { LyricPick } from './useLyricPick';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric panel; each part uses its own slice of it */
import styles from './LyricLinePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricLineGroupsProps {
  sections: ReadonlyArray<LyricSection>;
  suggestions: ReadonlyArray<LyricSuggestion>;
  pick: LyricPick;
}

/** The list of the panel: "Suggested" on top, then each section with its header and "×N". */
const LyricLineGroupsComponent: React.FC<LyricLineGroupsProps> = ({ sections, suggestions, pick }) => {
  const intl = useIntl();
  const tagsOf = (suggestion: LyricSuggestion): LyricRowTag[] => {
    const repeat: LyricRowTag = {
      text: intl.formatMessage(lyricPanelMessages.repeat, { count: suggestion.count }),
      grey: true,
    };
    if (suggestion.reason === 'hook') {
      return [{ text: intl.formatMessage(lyricPanelMessages.hook), grey: false }, repeat];
    }
    if (suggestion.reason === 'repeated') {
      return [repeat];
    }
    return [{ text: intl.formatMessage(lyricPanelMessages.short), grey: true }];
  };

  return (
    <>
      {suggestions.length > 0 && (
        <div className={`${styles.group} ${styles.groupSuggested}`}>
          <div className={styles.groupHead}>{intl.formatMessage(lyricPanelMessages.suggested)}</div>
          {suggestions.map((suggestion) => (
            <LyricLineRow
              key={`s-${suggestion.line}`}
              line={suggestion.line}
              state={pick.flatRowState(suggestion.line)}
              tags={tagsOf(suggestion)}
              onPick={() => pick.pick(suggestion.sectionIndex, suggestion.lineIndex)}
            />
          ))}
        </div>
      )}
      {sections.map((section, sectionIndex) => (
        // eslint-disable-next-line react/no-array-index-key -- sections have no id; the order is stable
        <div key={sectionIndex} className={styles.group}>
          <div className={styles.groupHead}>
            {sectionName(intl, sections, sectionIndex)}
            {section.repeat > 1 && (
              <span className={styles.repeat}>
                {intl.formatMessage(lyricPanelMessages.repeat, { count: section.repeat })}
              </span>
            )}
          </div>
          {section.lines.map((line, lineIndex) => (
            <LyricLineRow
              // eslint-disable-next-line react/no-array-index-key -- a section can hold the same line two times
              key={lineIndex}
              line={line}
              state={pick.sectionRowState(section.lines, lineIndex)}
              onPick={() => pick.pick(sectionIndex, lineIndex)}
            />
          ))}
        </div>
      ))}
    </>
  );
};

export const LyricLineGroups = TypedMemo(LyricLineGroupsComponent);
